import { Checkbox } from "@dash/form";
import { useTheme } from "@emotion/react";
import { useCallback, useEffect, useRef, useState, type FC } from "react";

export type SupportCategorizedCheckboxOption = {
  key: string;
  label: string;
};

export type SupportCategorizedCheckboxCategory = {
  id: string;
  label: string;
  options: SupportCategorizedCheckboxOption[];
};

type Props = {
  categories: SupportCategorizedCheckboxCategory[];
  selectedKeys: string[];
  onSelectedKeysChange: (selectedKeys: string[]) => void;
};

export const SupportCategorizedCheckboxList: FC<Props> = ({
  categories,
  selectedKeys,
  onSelectedKeysChange,
}) => {
  const theme = useTheme();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);

  const updateScrollState = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;
    setHasMoreBelow(element.scrollHeight - element.scrollTop - element.clientHeight > 12);
  }, []);

  useEffect(() => {
    updateScrollState();
    const element = scrollRef.current;
    if (!element) return;
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(element);
    return () => observer.disconnect();
  }, [categories, updateScrollState]);

  const onOptionToggle = (key: string, isSelected: boolean) => {
    if (isSelected) {
      onSelectedKeysChange([...selectedKeys, key]);
      return;
    }
    onSelectedKeysChange(selectedKeys.filter((selectedKey) => selectedKey !== key));
  };

  return (
    <div
      css={{
        position: "relative",
        flex: 1,
        minHeight: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        ref={scrollRef}
        onScroll={updateScrollState}
        css={{
          display: "flex",
          flexDirection: "column",
          gap: "1.25rem",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-y",
          paddingBottom: "1.5rem",
        }}
      >
        {categories.map((category) => (
          <div
            key={category.id}
            css={{
              display: "flex",
              flexDirection: "column",
              gap: "0.625rem",
            }}
          >
            <div
              css={{
                color: theme.colors.primary,
                fontFamily: theme.displayFontFamily,
                fontWeight: 600,
                fontSize: "0.9375rem",
              }}
            >
              {category.label}
            </div>
            <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem", paddingLeft: "0.125rem" }}>
              {category.options.map((option) => (
                <Checkbox
                  key={option.key}
                  label={option.label}
                  value={selectedKeys.includes(option.key)}
                  onChange={(value) => onOptionToggle(option.key, value)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      {hasMoreBelow ? (
        <div
          css={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: "2.75rem",
            pointerEvents: "none",
            background: `linear-gradient(to bottom, transparent, ${theme.colors.background})`,
          }}
        />
      ) : null}
    </div>
  );
};
